import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { PhoneCall, Copy, Loader2, Square, Trash2, Eye, RefreshCw, ChevronDown, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { parseNumberList } from '../../utils/numberCheckParse';

// בדיקת מספרים: מדביקים שורות בפורמט "... DID=07... | Password=... ..."
// והשרת בודק בימות המשיח לכל קו מה יש בשלוחה הראשית (ivr2:ext.ini),
// ומחזיר את הקווים שעדיין מפנים (routing_yemot) למספר היעד.
// הבדיקה נשמרת במסד ורצה בשרת ברקע (עד 10 קווים בדקה, ממשיכה גם אחרי הפעלה מחדש);
// כאן רק שואלים על ההתקדמות ומושכים תוצאות חדשות בהמשכים.

const DEFAULT_TARGET = '0747175868';
const MAX_LINES = 5000;
const POLL_MS = 5000;        // נבדקים 10 קווים בדקה, אז אין טעם לשאול לעתים קרובות יותר
const FAILS_BEFORE_NOTICE = 3; // תקלה רגעית (הפעלה מחדש של השרת וכו') לא מקפיצה הודעה
const OPEN_KEY = 'numberCheckOpen';

// פתוח/סגור נשמר בדפדפן (נוחות בלבד; בלי גישה ל-localStorage פשוט מתחיל סגור)
const readOpen = () => { try { return localStorage.getItem(OPEN_KEY) === '1'; } catch (_) { return false; } };
const writeOpen = (v) => { try { localStorage.setItem(OPEN_KEY, v ? '1' : '0'); } catch (_) { /* לא חשוב */ } };

const STATUS = {
  match: { label: 'מפנה ליעד', cls: 'bg-green-100 text-green-700' },
  other: { label: 'לא מפנה', cls: 'bg-gray-100 text-gray-600' },
  login_error: { label: 'סיסמה שגויה', cls: 'bg-amber-100 text-amber-700' },
  error: { label: 'שגיאה', cls: 'bg-red-100 text-red-700' },
  skipped: { label: 'לא נבדק (נעצר)', cls: 'bg-gray-50 text-gray-400' }
};

const JOB_STATUS = {
  running: 'רצה',
  done: 'הסתיימה',
  cancelled: 'נעצרה'
};

const FILTERS = [
  { id: 'all', label: 'הכל' },
  { id: 'match', label: 'מפנים' },
  { id: 'login_error', label: 'סיסמה שגויה' },
  { id: 'other', label: 'לא מפנים' },
  { id: 'error', label: 'שגיאות' }
];

const fmtTime = (ms) => (ms ? new Date(ms).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '');

function fmtDuration(min) {
  if (min < 60) return `כ-${Math.max(1, Math.ceil(min))} דקות`;
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  return `כ-${h} שעות${m ? ` ו-${m} דקות` : ''}`;
}

export default function NumberCheckTab() {
  const [text, setText] = useState('');
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);       // סיכום הבדיקה המוצגת
  const [items, setItems] = useState([]);     // תוצאות שנטענו עד עכשיו
  const [filter, setFilter] = useState('all');
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(readOpen);
  const [connLost, setConnLost] = useState(false);
  const toggleOpen = () => setOpen((v) => { writeOpen(!v); return !v; });
  // הפענוח בדפדפן (כל פורמט של מספר וסיסמה); לשרת נשלחת רק הרשימה {did, password}
  const parsed = useMemo(() => parseNumberList(text), [text]);
  const lines = parsed.lines;
  const detected = lines.length;
  const [notice, setNotice] = useState(null); // תשובת השרת "חלק מהקווים כבר נבדקו"
  useEffect(() => setNotice(null), [text, target]);

  const viewRef = useRef({ id: null, lastSeq: 0, timer: null, busy: false });

  const loadJobs = useCallback(async () => {
    try {
      const res = await api.get('/number-check');
      setJobs(res.data);
      return res.data;
    } catch (_) {
      return [];
    }
  }, []);

  const stopPolling = () => {
    clearTimeout(viewRef.current.timer);
    viewRef.current.timer = null;
  };

  // משיכה אחת: סיכום + תוצאות חדשות מ-lastSeq. ממשיכים כל עוד הבדיקה רצה או שיש עוד תוצאות לטעון.
  const fetchJob = useCallback(async (id) => {
    const v = viewRef.current;
    if (v.id !== id || v.busy) return;
    v.busy = true;
    try {
      const res = await api.get(`/number-check/${id}`, { params: { after: v.lastSeq } });
      if (v.id !== id) return;
      const d = res.data;
      if (d.items.length) {
        v.lastSeq = d.items[d.items.length - 1].seq;
        setItems((prev) => prev.concat(d.items));
      }
      setJob(d);
      // עדכון השורה ברשימת הבדיקות (ובכותרת כשהחלק סגור) בלי לטעון את כל הרשימה מחדש
      setJobs((prev) => prev.map((j) => (j.id === d.id
        ? { ...j, status: d.status, done: d.done, total: d.total, matchesCount: d.matchesCount, finishedAt: d.finishedAt }
        : j)));
      setNow(Date.now());
      const more = d.status === 'running' || v.lastSeq < d.total;
      v.fails = 0;
      setConnLost(false);
      if (more) {
        // כשהלשונית של הדפדפן ברקע לא שואלים; ממשיכים כשחוזרים אליה
        const tick = () => { if (document.hidden) v.timer = setTimeout(tick, POLL_MS); else fetchJob(id); };
        v.timer = setTimeout(tick, d.hasMore ? 200 : POLL_MS);
      } else {
        loadJobs();
      }
    } catch (e) {
      if (v.id === id) {
        if (e.response?.status === 404) {
          toast.error('הבדיקה לא נמצאה (אולי נמחקה)');
          loadJobs();
          return;
        }
        v.fails = (v.fails || 0) + 1;
        if (v.fails === FAILS_BEFORE_NOTICE) setConnLost(true);
        // הבדיקה עצמה ממשיכה בשרת; רק מנסים שוב, בהדרגה לאט יותר (עד דקה)
        v.timer = setTimeout(() => fetchJob(id), Math.min(60000, POLL_MS * 2 ** Math.min(v.fails, 4)));
      }
    } finally {
      v.busy = false;
    }
  }, [loadJobs]);

  const openJob = useCallback((id) => {
    stopPolling();
    viewRef.current = { id, lastSeq: 0, timer: null, busy: false, fails: 0 };
    setConnLost(false);
    setItems([]);
    setJob(null);
    setFilter('all');
    fetchJob(id);
  }, [fetchJob]);

  // בכניסה ללשונית: אם יש בדיקה שרצה - מציגים אותה
  useEffect(() => {
    loadJobs().then((list) => {
      const running = list.find(j => j.status === 'running');
      if (running && !viewRef.current.id) openJob(running.id);
    });
    return stopPolling;
  }, [loadJobs, openJob]);

  // ספירה לאחור עד האצווה הבאה
  useEffect(() => {
    if (!job?.nextBatchAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job?.nextBatchAt]);

  const start = async (mode) => {
    setStarting(true);
    setNotice(null);
    try {
      const res = await api.post('/number-check', { lines, target, mode });
      toast.success(`הבדיקה התחילה: ${res.data.total} קווים${res.data.skippedKnown ? ` (${res.data.skippedKnown} שכבר נבדקו דולגו)` : ''}`);
      setText('');
      loadJobs();
      openJob(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'הבדיקה נכשלה');
    } finally {
      setStarting(false);
    }
  };

  // לפני שמתחילים: האם הקווים האלה כבר נבדקו? אם כן - מציגים הודעה עם בחירה
  const run = async () => {
    if (detected > MAX_LINES) {
      toast.error(`עד ${MAX_LINES} קווים בבדיקה אחת`);
      return;
    }
    setStarting(true);
    try {
      const res = await api.post('/number-check/preview', { lines, target });
      if (res.data.done + res.data.queued === 0) {
        setStarting(false);
        await start('new');
        return;
      }
      setNotice(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'הבדיקה נכשלה');
    }
    setStarting(false);
  };

  const stop = async (id) => {
    try {
      await api.post(`/number-check/${id}/stop`);
      toast('הבדיקה נעצרה');
      loadJobs();
      if (viewRef.current.id === id && !viewRef.current.timer) fetchJob(id);
    } catch (e) {
      toast.error(e.response?.data?.error || 'העצירה נכשלה');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('למחוק את הבדיקה ואת כל התוצאות שלה?')) return;
    try {
      await api.delete(`/number-check/${id}`);
      if (viewRef.current.id === id) {
        stopPolling();
        viewRef.current = { id: null, lastSeq: 0, timer: null, busy: false };
        setJob(null);
        setItems([]);
      }
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.error || 'המחיקה נכשלה');
    }
  };

  // הורדת כל התוצאות כ-CSV (דרך api כדי שיישלח טוקן ההתחברות)
  const downloadCsv = async (id) => {
    try {
      const res = await api.get(`/number-check/${id}/csv`, { responseType: 'blob' });
      const name = (res.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'number-check.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error('הורדת הקובץ נכשלה');
    }
  };

  const copyMatches = async () => {
    try {
      await navigator.clipboard.writeText(job.matches.join('\n'));
      toast.success('הרשימה הועתקה');
    } catch (_) {
      toast.error('ההעתקה נכשלה');
    }
  };

  const running = job?.status === 'running';
  const waitSec = job?.nextBatchAt ? Math.max(0, Math.ceil((job.nextBatchAt - now) / 1000)) : 0;
  const shown = filter === 'all' ? items : items.filter(r => r.status === filter);

  const runningJob = jobs.find(j => j.status === 'running');

  return (
    <div className="bg-white rounded-lg shadow">
      {/* הכותרת היא לחצן שפותח/סוגר; כשסגור רואים רק את מצב הבדיקה שרצה */}
      <button
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 p-4 sm:p-6 text-right hover:bg-gray-50 rounded-lg"
      >
        <PhoneCall className="w-5 h-5 text-blue-600 shrink-0" />
        <h2 className="text-2xl font-bold text-gray-900">בדיקת מספרים</h2>
        {runningJob && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-sm">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            רצה: {runningJob.done.toLocaleString('he-IL')} / {runningJob.total.toLocaleString('he-IL')}
            {' · '}{runningJob.matchesCount} מפנים
          </span>
        )}
        <ChevronDown className={`w-5 h-5 text-gray-500 mr-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
      <div className="border-t border-gray-100 p-4 sm:p-6 space-y-6">
      <div className="space-y-4">
        <div className="text-sm text-gray-600 space-y-2">
          <p>
            מדביקים שורה לכל קו, עם מספר וסיסמה בכל פורמט (עד {MAX_LINES.toLocaleString('he-IL')} קווים), למשל:
          </p>
          <pre dir="ltr" className="bg-gray-50 rounded-lg p-2 text-xs font-mono text-gray-700 overflow-x-auto">{`0733517274:303030
0733517274 303030
073-351-7274 - 303030
DID=0733517274 | Password=303030`}</pre>
          <p>
            לכל קו נבדקת השלוחה הראשית בימות המשיח (<code dir="ltr">ivr2:ext.ini</code>), ומוחזרים הקווים שמוגדרים בה
            {' '}<code dir="ltr">type=routing_yemot</code> למספר היעד. נבדקים עד 10 קווים בדקה, והבדיקה רצה בשרת וממשיכה גם אם
            סוגרים את הדפדפן או שהשרת מופעל מחדש. קו שכבר נבדק יזוהה, ותוכל לבחור אם לבדוק אותו שוב.
          </p>
          <p>
            התוצאות, שם הלקוח והסיסמאות נשמרים בבסיס הנתונים של הבוט (והסיסמאות מופיעות בקובץ ה-CSV) עד שמוחקים את הבדיקה.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">מספר יעד לראוטינג</label>
          <input
            dir="ltr"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            רשימת הקווים
            {detected > 0 && (
              <span className={`font-normal ${detected > MAX_LINES ? 'text-red-600' : 'text-gray-500'}`}>
                {' '}({detected.toLocaleString('he-IL')} קווים זוהו, {fmtDuration(detected / 10)})
              </span>
            )}
          </label>
          <textarea
            dir="ltr"
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'07XXXXXXXX:XXXXXX\n07XXXXXXXX XXXXXX\n[2025-09-10 05:53:56] ApiPhone=05XXXXXXXX | DID=07XXXXXXXX | Password=XXXXXX | תוצאה=הצליח'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          {parsed.duplicates > 0 && (
            <p className="mt-1 text-xs text-gray-500">{parsed.duplicates} שורות כפולות ברשימה יבדקו פעם אחת.</p>
          )}
          {parsed.unparsed.length > 0 && (
            <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <p className="font-medium">{parsed.unparsed.length} שורות לא זוהו ולא ייבדקו:</p>
              <ul className="mt-1 space-y-0.5 text-xs" dir="ltr">
                {parsed.unparsed.slice(0, 5).map((u) => (
                  <li key={u.n} className="font-mono truncate">שורה {u.n}: {u.text} ({u.reason})</li>
                ))}
                {parsed.unparsed.length > 5 && <li>ועוד {parsed.unparsed.length - 5}...</li>}
              </ul>
            </div>
          )}
        </div>

        {notice && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3">
            <p className="font-semibold text-amber-900">
              {notice.fresh === 0
                ? 'כל הקווים האלה כבר נבדקו'
                : `${(notice.done + notice.queued).toLocaleString('he-IL')} מתוך ${notice.total.toLocaleString('he-IL')} קווים כבר נבדקו`}
            </p>
            <ul className="text-sm text-amber-900 list-disc pr-5 space-y-0.5">
              {notice.done > 0 && (
                <li>
                  {notice.done.toLocaleString('he-IL')} כבר נבדקו (לאחרונה {fmtTime(notice.lastCheckedAt)}), {notice.doneMatches.toLocaleString('he-IL')} מהם מפנים ל-<span dir="ltr">{notice.target}</span>
                </li>
              )}
              {notice.queued > 0 && <li>{notice.queued.toLocaleString('he-IL')} כבר ממתינים בבדיקה שרצה עכשיו</li>}
              {notice.fresh > 0 && <li>{notice.fresh.toLocaleString('he-IL')} חדשים, שעוד לא נבדקו</li>}
            </ul>
            <div className="flex flex-wrap gap-2">
              {notice.fresh > 0 && (
                <button onClick={() => start('new')} disabled={starting}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50">
                  בדוק רק את החדשים ({notice.fresh.toLocaleString('he-IL')})
                </button>
              )}
              <button onClick={() => start('all')} disabled={starting}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-50">
                בדוק הכל בכל זאת ({notice.total.toLocaleString('he-IL')})
              </button>
              {notice.bestJobId && (
                <button onClick={() => { openJob(notice.bestJobId); setNotice(null); }}
                  className="px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-900 text-sm hover:bg-amber-100">
                  הצג את התוצאות הקודמות
                </button>
              )}
              <button onClick={() => setNotice(null)} className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-amber-100">ביטול</button>
            </div>
          </div>
        )}

        <button
          onClick={run}
          disabled={starting || detected === 0 || !!notice}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {starting ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <PhoneCall className="w-4 h-4 ml-2" />}
          התחל בדיקה
        </button>
      </div>

      {jobs.length > 0 && (
        <div className="rounded-lg border border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">בדיקות</h3>
            <button onClick={loadJobs} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" title="רענון">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-right font-medium">התחילה</th>
                <th className="px-4 py-2 text-right font-medium">יעד</th>
                <th className="px-4 py-2 text-right font-medium">נבדקו</th>
                <th className="px-4 py-2 text-right font-medium">מפנים</th>
                <th className="px-4 py-2 text-right font-medium">מצב</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.map((j) => (
                <tr key={j.id} className={job?.id === j.id ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-2 whitespace-nowrap">{fmtTime(j.createdAt)}</td>
                  <td className="px-4 py-2 font-mono" dir="ltr">{j.target}</td>
                  <td className="px-4 py-2">{j.done} / {j.total}</td>
                  <td className="px-4 py-2 font-semibold text-green-700">{j.matchesCount}</td>
                  <td className="px-4 py-2">{JOB_STATUS[j.status] || j.status}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-left">
                    <button onClick={() => openJob(j.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="הצג">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => downloadCsv(j.id)} className="p-1.5 text-green-700 hover:bg-green-50 rounded-lg" title="הורדת התוצאות כ-CSV">
                      <Download className="w-4 h-4" />
                    </button>
                    {j.status === 'running' && (
                      <button onClick={() => stop(j.id)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg" title="עצור">
                        <Square className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={() => remove(j.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="מחק">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {job && (
        <>
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
              {running && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
              <span>
                נבדקו {job.done.toLocaleString('he-IL')} מתוך {job.total.toLocaleString('he-IL')}
                {running && job.total - job.done > 0 && ` · נשארו ${fmtDuration((job.total - job.done) / 10)}`}
                {running && waitSec > 0 && ` · האצווה הבאה בעוד ${waitSec} שניות`}
                {!running && ` · ${JOB_STATUS[job.status] || job.status}`}
              </span>
              {connLost && (
                <span className="text-amber-700">אין כרגע חיבור לשרת; הבדיקה ממשיכה שם, מנסה להתחבר שוב...</span>
              )}
              {running && (
                <button onClick={() => stop(job.id)} className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg">
                  <Square className="w-4 h-4 ml-1" />
                  עצור
                </button>
              )}
              {job.done > 0 && (
                <button onClick={() => downloadCsv(job.id)} className="inline-flex items-center px-3 py-1.5 text-sm text-green-700 hover:bg-green-50 rounded-lg">
                  <Download className="w-4 h-4 ml-1" />
                  הורד CSV{running ? ' (מה שנבדק עד עכשיו)' : ''}
                </button>
              )}
            </div>
            {job.total > 0 && (
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.round((job.done / job.total) * 100)}%` }} />
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <h3 className="font-semibold text-gray-900">
                קווים שעדיין מפנים ל-<span dir="ltr">{job.target}</span> ({job.matches.length}
                {job.done < job.total ? ' עד עכשיו' : ''})
              </h3>
              {job.matches.length > 0 && (
                <button onClick={copyMatches} className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Copy className="w-4 h-4 ml-1" />
                  העתק
                </button>
              )}
            </div>
            {job.matches.length > 0 ? (
              <pre dir="ltr" className="bg-gray-50 rounded-lg p-3 text-sm font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">{job.matches.join('\n')}</pre>
            ) : (
              <p className="text-sm text-gray-500">{job.done ? 'אף קו לא מפנה ליעד.' : 'עוד לא נבדקו קווים.'}</p>
            )}
          </div>

          {items.length > 0 && (
            <div className="rounded-lg border border-gray-200">
              <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-gray-100">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1 rounded-full text-sm ${filter === f.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {f.label}
                    {f.id !== 'all' && job.counts?.[f.id] ? ` (${job.counts[f.id]})` : ''}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-right font-medium">#</th>
                      <th className="px-4 py-2 text-right font-medium">DID</th>
                      <th className="px-4 py-2 text-right font-medium">שם</th>
                      <th className="px-4 py-2 text-right font-medium">מצב</th>
                      <th className="px-4 py-2 text-right font-medium">type</th>
                      <th className="px-4 py-2 text-right font-medium">routing_yemot_number / פירוט</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {shown.map((r) => {
                      const s = STATUS[r.status] || STATUS.error;
                      return (
                        <tr key={r.seq}>
                          <td className="px-4 py-2 text-gray-400">{r.seq}</td>
                          <td className="px-4 py-2 font-mono" dir="ltr">{r.did}</td>
                          <td className="px-4 py-2">{r.name || ''}</td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${s.cls}`}>{s.label}</span></td>
                          <td className="px-4 py-2 font-mono" dir="ltr">{r.type || ''}</td>
                          <td className="px-4 py-2 font-mono text-gray-600" dir="ltr">{r.routingNumber || r.detail || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      </div>
      )}
    </div>
  );
}
